import React, { useState } from 'react';
import {
  Dialog,
  DialogActions,
  Button,
  TextField,
  Grid,
  IconButton,
  Box,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AuthorizationClient from '../single-page/api/AuthorizationClient.js';
import { TextMaskDocument, TextMaskPhone, TextMaskName } from './TextMaskCustom.jsx';
import grafismo from '../../assets/images/grafismo.svg';
import { OFFICIAL_CONTACTS } from './Footer';

/** Official support inbox — never use mock placeholders. */
const SUPPORT_EMAIL = OFFICIAL_CONTACTS.email;

const inputStyles = {
  marginBottom: '12px',
  width: {
    xs: '95%',
    sm: '90%',
    md: '80%'
  },
  '& .MuiOutlinedInput-root': {
    border: '1.68px solid #012F4740',
    borderRadius: '8px',
    transition: 'all 0.2s ease-in-out',
    '& fieldset': {
      border: 'none'
    },
    '&:hover': {
      borderColor: '#012F47'
    },
    '&.Mui-focused': {
      borderColor: '#012F47'
    }
  },
  '& .MuiInputLabel-root': {
    color: '#666',
    '&.Mui-focused': {
      color: '#012F47'
    }
  },
  '& .MuiInputLabel-outlined': {
    '&.MuiInputLabel-shrink': {
      background: 'white',
      padding: '0 8px',
      marginLeft: '-4px',
      transform: 'translate(14px, -9px) scale(0.75)'
    }
  }
};

const ContactUsModal = ({ open, onClose }) => {
  const [user, setUser] = useState({
    name: '',
    email: '',
    document: '',
    phone: '',
    message: ''
  });
  
  const [userErrors, setUserErrors] = useState({});

  const handleChange = (e) => {
    if (e?.target?.name) {
      setUser(prev => ({
        ...prev,
        [e.target.name]: e.target.value
      }));
    } 
    else if (typeof e === 'object' && e.name) {
      setUser(prev => ({
        ...prev,
        [e.name]: e.value
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const errors = {};
    if (!user.name) errors.name = ['Nome é obrigatório'];
    if (!user.email) errors.email = ['Email é obrigatório'];
    if (!user.document) errors.document = ['CPF/CNPJ é obrigatório'];
    if (!user.phone) errors.phone = ['Telefone é obrigatório'];
    if (!user.message) errors.message = ['Mensagem é obrigatória'];

    if (Object.keys(errors).length > 0) {
      setUserErrors(errors);
      return;
    }

    if (!SUPPORT_EMAIL) {
      setUserErrors({
        message: [
          'Canal de suporte ainda não configurado. Tente novamente mais tarde ou use outro meio oficial de contato.',
        ],
      });
      return;
    }

    const subject = "Contato - Suporte";
    const body = `
Olá, equipe de suporte!

Solicito ajuda e segue abaixo meus dados para contato:

DADOS PARA CONTATO
-----------------
Nome: ${user.name}
E-mail: ${user.email}
CPF/CNPJ: ${user.document}
Telefone: ${user.phone}

DETALHES DA SOLICITAÇÃO
----------------------
${user.message}

Agradeço desde já a atenção.

Atenciosamente,
${user.name}
    `.trim();

    const mailtoLink = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    window.location.href = mailtoLink;
    onClose();
  };

  const getTextFieldProps = (name, label, mask = null) => ({
    name,
    label,
    value: user[name],
    onChange: handleChange,
    required: true,
    error: !!userErrors[name],
    helperText: userErrors[name]?.join(', '),
    sx: inputStyles,
    autoComplete: "off",
    ...(mask && {
      InputProps: {
        inputComponent: mask,
        inputProps: {
          name,
          onChange: handleChange
        }
      }
    })
  });

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        style: { 
          borderRadius: 8,
          margin: '16px'
        }
      }}
    >
      <Grid container>
        <Grid 
          item 
          xs={2}
          sm={3}
          md={4}
          sx={{ 
            backgroundColor: '#012F47',
            minHeight: { 
              xs: '150px',
              sm: '400px',
              md: '500px'
            },
            position: 'relative',
            display: 'block'
          }}
        >
          <Box
            component="img"
            src={grafismo}
            alt="Grafismo"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              objectFit: 'contain'
            }}
          />
        </Grid>

        <Grid 
          item 
          xs={10}
          sm={9}
          md={8}
          style={{ position: 'relative' }}
        >
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'grey.500'
            }}
          >
            <CloseIcon />
          </IconButton>

          <Box sx={{ 
            p: { 
              xs: 2,
              sm: 3,
              md: 4  
            },
            pt: { 
              xs: 4,
              sm: 5 
            },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}>
            <Typography 
              variant="h4" 
              sx={{ 
                mb: 3,
                textAlign: 'center',
                fontWeight: 600,
                fontSize: {
                  xs: '1.5rem',
                  sm: '2rem'
                }
              }}
            >
              Encaminhando você para o melhor suporte
            </Typography>

            <form 
              onSubmit={handleSubmit} 
              style={{ 
                width: '100%', 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center' 
              }}
              autoComplete="off"
            >
              <TextField
                {...getTextFieldProps('name', 'Nome', TextMaskName)}
              />

              <TextField
                {...getTextFieldProps('email', 'E-mail')}
              />
              
              <TextField
                {...getTextFieldProps('document', 'CPF ou CNPJ', TextMaskDocument)}
              />

              <TextField
                {...getTextFieldProps('phone', 'Celular', TextMaskPhone)}
              />
            
              <TextField
                {...getTextFieldProps('message', 'Descrição')}
                multiline
                rows={4}
                sx={{
                  ...inputStyles,
                  '& .MuiOutlinedInput-root': {
                    ...inputStyles['& .MuiOutlinedInput-root'],
                    height: 'auto',
                    minHeight: '120px',
                    alignItems: 'flex-start'
                  },
                  '& .MuiInputBase-input': {
                    height: 'auto !important',
                    padding: '12px',
                    lineHeight: '1.5'
                  },
                  '& textarea': {
                    resize: 'vertical',
                    minHeight: '100px !important'
                  }
                }}
                InputProps={{
                  ...getTextFieldProps('message', 'Descrição').InputProps,
                  style: { height: 'auto' }
                }}
              />

              <DialogActions sx={{ 
                mt: 3,
                padding: 0,
                display: 'flex',
                justifyContent: 'center',
                gap: { 
                  xs: 1,
                  sm: 2 
                },
                width: {
                  xs: '95%',
                  sm: '90%',
                  md: '80%'
                },
                flexDirection: {
                  xs: 'column',
                  sm: 'row'
                },
                '& .MuiButton-root': {
                  xs: {
                    width: '100%',
                    margin: '4px 0',
                  }
                }
              }}>
                <Button 
                  onClick={handleSubmit} 
                  variant="contained"
                  sx={{ 
                    bgcolor: '#4CAF50',
                    padding: {
                      xs: '10px 20px',
                      sm: '12px 40px'
                    },
                    fontSize: {
                      xs: '0.9rem',
                      sm: '1rem'
                    },
                    textTransform: 'none',
                    borderRadius: '6px',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      bgcolor: '#388E3C',
                    }
                  }}
                >
                  Enviar
                </Button>
              </DialogActions>
            </form>
          </Box>
        </Grid>
      </Grid>
    </Dialog>
  );
};

export default ContactUsModal;